// src/components/themes/default/admin/BranchChatPanel.tsx
"use client";

import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import Swal from "sweetalert2";
import {
  MessageSquare,
  Users,
  Store,
  Building2,
  Paperclip,
  Phone,
  Video,
  Send,
  Loader2,
  ChevronDown,
  RefreshCw,
  Search,
  CheckCheck,
  Mic,
  PhoneOff,
  MicOff,
  VideoOff,
  PhoneCall,
  Trash2,
  PlusCircle,
  ExternalLink,
} from "lucide-react";

interface BranchChatPanelProps {
  subdomain: string;
  branchId: number;
  branchSlug?: string;
  branchName?: string;
  theme?: "dark" | "light";
  primaryColor?: string;
  initialChannel?: "branch_merchant" | "branch_customer";
}

// 🌟 Google Public STUN Servers
const ICE_SERVERS: RTCConfiguration = {
  iceServers: [
    { urls: "stun:stun.l.google.com:19302" },
    { urls: "stun:stun1.l.google.com:19302" },
  ],
};

// 🌟 Web Audio API Call Tone Generator
class CallTonePlayer {
  private ctx: AudioContext | null = null;
  private intervalId: any = null;

  playIncoming() {
    this.stop();
    try {
      const AudioCtx = window.AudioContext || (window as any).webkitAudioContext;
      if (!AudioCtx) return;
      this.ctx = new AudioCtx();
      const playChime = () => {
        if (!this.ctx || this.ctx.state === "closed") return;
        const now = this.ctx.currentTime;
        [853, 960].forEach((freq) => {
          const osc = this.ctx!.createOscillator();
          const gain = this.ctx!.createGain();
          osc.type = "sine";
          osc.frequency.setValueAtTime(freq, now);
          gain.gain.setValueAtTime(0.18, now);
          gain.gain.exponentialRampToValueAtTime(0.001, now + 1.2);
          osc.connect(gain);
          gain.connect(this.ctx!.destination);
          osc.start(now);
          osc.stop(now + 1.2);
        });
      };
      playChime();
      this.intervalId = setInterval(playChime, 2400);
    } catch {}
  }

  playOutgoing() {
    this.stop();
    try {
      const AudioCtx = window.AudioContext || (window as any).webkitAudioContext;
      if (!AudioCtx) return;
      this.ctx = new AudioCtx();
      const playBeep = () => {
        if (!this.ctx || this.ctx.state === "closed") return;
        const now = this.ctx.currentTime;
        const osc = this.ctx!.createOscillator();
        const gain = this.ctx!.createGain();
        osc.type = "sine";
        osc.frequency.setValueAtTime(425, now);
        gain.gain.setValueAtTime(0.12, now);
        gain.gain.setValueAtTime(0.12, now + 0.8);
        gain.gain.exponentialRampToValueAtTime(0.001, now + 0.85);
        osc.connect(gain);
        gain.connect(this.ctx!.destination);
        osc.start(now);
        osc.stop(now + 0.85);
      };
      playBeep();
      this.intervalId = setInterval(playBeep, 2600);
    } catch {}
  }

  stop() {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
    if (this.ctx && this.ctx.state !== "closed") {
      this.ctx.close().catch(() => {});
      this.ctx = null;
    }
  }
}

const tonePlayer = new CallTonePlayer();

// 🌟 Clickable Link Renderer (Adaptive Light & Dark Theme)
function FormattedChatText({ content, isLight }: { content: string; isLight: boolean }) {
  if (!content) return null;
  const lines = content.split("\n");

  return (
    <div className="space-y-1">
      {lines.map((line, lIdx) => {
        const linkRegex = /\[(.*?)\]\((.*?)\)/g;
        const parts: any[] = [];
        let lastIndex = 0;
        let match;

        while ((match = linkRegex.exec(line)) !== null) {
          if (match.index > lastIndex) {
            parts.push(line.substring(lastIndex, match.index));
          }
          const linkText = match[1];
          const linkUrl = match[2];
          parts.push(
            <a
              key={match.index}
              href={linkUrl}
              className={`inline-flex items-center gap-1.5 font-bold px-2.5 py-1 rounded-xl text-[11px] my-1 transition-all cursor-pointer border active:scale-95 shadow-xs ${
                isLight
                  ? "text-emerald-700 bg-emerald-50 hover:bg-emerald-100 border-emerald-300"
                  : "text-emerald-300 bg-black/40 hover:bg-black/60 border-emerald-500/30"
              }`}
            >
              <span>{linkText}</span>
              <ExternalLink className="w-3 h-3 text-emerald-500" />
            </a>
          );
          lastIndex = linkRegex.lastIndex;
        }

        if (lastIndex < line.length) {
          parts.push(line.substring(lastIndex));
        }

        return (
          <p key={lIdx} className="leading-relaxed break-words">
            {parts.map((part, pIdx) => {
              if (typeof part === "string") {
                const boldParts = part.split(/(\*\*.*?\*\*)/g);
                return boldParts.map((bPart, bIdx) => {
                  if (bPart.startsWith("**") && bPart.endsWith("**")) {
                    return (
                      <strong
                        key={bIdx}
                        className={`font-bold ${isLight ? "text-slate-900" : "text-white"}`}
                      >
                        {bPart.slice(2, -2)}
                      </strong>
                    );
                  }
                  return bPart;
                });
              }
              return part;
            })}
          </p>
        );
      })}
    </div>
  );
}

export function BranchChatPanel({
  subdomain,
  branchId,
  branchSlug,
  branchName,
  theme: propTheme,
  primaryColor = "#059669",
  initialChannel = "branch_merchant",
}: BranchChatPanelProps) {
  // 🌟 Dynamic Theme Auto-Detection (Prop + DOM class + localStorage)
  const [currentTheme, setCurrentTheme] = useState<"dark" | "light">(propTheme || "dark");

  useEffect(() => {
    if (propTheme) {
      setCurrentTheme(propTheme);
      return;
    }

    const checkTheme = () => {
      if (typeof window === "undefined") return;
      const isDocLight =
        document.documentElement.classList.contains("light") ||
        document.documentElement.classList.contains("admin-light-theme") ||
        localStorage.getItem("admin_theme") === "light";
      setCurrentTheme(isDocLight ? "light" : "dark");
    };

    checkTheme();
    const observer = new MutationObserver(checkTheme);
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ["class"] });
    window.addEventListener("storage", checkTheme);

    return () => {
      observer.disconnect();
      window.removeEventListener("storage", checkTheme);
    };
  }, [propTheme]);

  const isLight = currentTheme === "light";

  // 🌟 Branch Manager Authentication Isolation
  const branchManager = useMemo(() => {
    if (typeof window === "undefined") {
      return { token: "", id: null, name: branchName || "Branch Outlet" };
    }
    try {
      if (branchSlug) {
        const exactKey = `branch_auth_${subdomain}_${branchSlug}`;
        const exactData = JSON.parse(localStorage.getItem(exactKey) || "{}");
        if (exactData?.token && exactData?.user?.id) {
          return {
            token: exactData.token,
            id: exactData.user.id,
            name: exactData.user.first_name || branchName || "Branch Outlet",
          };
        }
      }
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith("branch_auth_")) {
          const data = JSON.parse(localStorage.getItem(key) || "{}");
          if (data?.token && data?.user?.id) {
            return {
              token: data.token,
              id: data.user.id,
              name: data.user.first_name || branchName || "Branch Outlet",
            };
          }
        }
      }
    } catch {}
    return { token: "", id: null, name: branchName || "Branch Outlet" };
  }, [branchName, branchSlug, subdomain]);

  const [channelType, setChannelType] = useState<"branch_merchant" | "branch_customer">(
    initialChannel
  );
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  useEffect(() => {
    if (initialChannel) {
      setChannelType(initialChannel);
    }
  }, [initialChannel]);

  const [rooms, setRooms] = useState<any[]>([]);
  const [selectedRoom, setSelectedRoom] = useState<any | null>(null);
  const [messages, setMessages] = useState<any[]>([]);
  const [loadingRooms, setLoadingRooms] = useState(true);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [messageText, setMessageText] = useState("");
  const [sending, setSending] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const [isRecording, setIsRecording] = useState(false);
  const [recordingDuration, setRecordingDuration] = useState(0);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const recordingTimerRef = useRef<any>(null);
  const isCancelledRef = useRef(false);

  const [callState, setCallState] = useState<"idle" | "calling" | "ringing" | "active">("idle");
  const [callType, setCallType] = useState<"audio" | "video">("audio");
  const [callDuration, setCallDuration] = useState(0);
  const [isMuted, setIsMuted] = useState(false);
  const [isVideoMuted, setIsVideoMuted] = useState(false);

  const socketRef = useRef<WebSocket | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const peerConnectionRef = useRef<RTCPeerConnection | null>(null);
  const localStreamRef = useRef<MediaStream | null>(null);
  const remoteStreamRef = useRef<MediaStream | null>(null);
  const localVideoRef = useRef<HTMLVideoElement>(null);
  const remoteVideoRef = useRef<HTMLVideoElement>(null);
  const remoteAudioRef = useRef<HTMLAudioElement>(null);
  const pendingCandidatesRef = useRef<RTCIceCandidateInit[]>([]);

  const getApiUrl = useCallback(
    (endpoint: string) => {
      const isLocal =
        typeof window !== "undefined" &&
        (window.location.hostname.includes("localhost") || window.location.hostname === "127.0.0.1");
      return isLocal ? `http://${subdomain}.localhost:8000${endpoint}` : endpoint;
    },
    [subdomain]
  );

  const getWsUrl = useCallback(
    (roomId: number) => {
      const isLocal =
        typeof window !== "undefined" &&
        (window.location.hostname.includes("localhost") || window.location.hostname === "127.0.0.1");
      if (isLocal) {
        return `ws://${subdomain}.localhost:8000/ws/chat/${roomId}/?token=${branchManager.token}`;
      }
      const proto = window.location.protocol === "https:" ? "wss:" : "ws:";
      return `${proto}//${window.location.host}/ws/chat/${roomId}/?token=${branchManager.token}`;
    },
    [branchManager.token, subdomain]
  );

  useEffect(() => {
    if (callState === "ringing") {
      tonePlayer.playIncoming();
    } else if (callState === "calling") {
      tonePlayer.playOutgoing();
    } else {
      tonePlayer.stop();
    }
    return () => tonePlayer.stop();
  }, [callState]);

  useEffect(() => {
    let interval: any;
    if (callState === "active") {
      interval = setInterval(() => setCallDuration((p) => p + 1), 1000);
    } else {
      setCallDuration(0);
    }
    return () => clearInterval(interval);
  }, [callState]);

  const formatTimer = (sec: number) => {
    const m = Math.floor(sec / 60).toString().padStart(2, "0");
    const s = (sec % 60).toString().padStart(2, "0");
    return `${m}:${s}`;
  };

  const cleanupMedia = useCallback(() => {
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach((t) => t.stop());
      localStreamRef.current = null;
    }
    if (peerConnectionRef.current) {
      peerConnectionRef.current.close();
      peerConnectionRef.current = null;
    }
    remoteStreamRef.current = null;
    if (remoteAudioRef.current) remoteAudioRef.current.srcObject = null;
    if (remoteVideoRef.current) remoteVideoRef.current.srcObject = null;
    if (localVideoRef.current) localVideoRef.current.srcObject = null;
    pendingCandidatesRef.current = [];
    tonePlayer.stop();
  }, []);

  const createPeerConnection = useCallback(
    (currentSocket: WebSocket, type: "audio" | "video") => {
      const pc = new RTCPeerConnection(ICE_SERVERS);

      pc.ontrack = (event) => {
        const remoteStream = event.streams[0];
        remoteStreamRef.current = remoteStream;

        if (remoteAudioRef.current) {
          remoteAudioRef.current.srcObject = remoteStream;
          remoteAudioRef.current.play().catch(() => {});
        }
        if (type === "video" && remoteVideoRef.current) {
          remoteVideoRef.current.srcObject = remoteStream;
        }
      };

      pc.onicecandidate = (event) => {
        if (event.candidate && currentSocket.readyState === WebSocket.OPEN) {
          currentSocket.send(
            JSON.stringify({
              type: "ice_candidate",
              data: { candidate: event.candidate, sender_id: branchManager.id },
            })
          );
        }
      };

      peerConnectionRef.current = pc;
      return pc;
    },
    [branchManager.id]
  );

  const fetchRooms = useCallback(async () => {
    setLoadingRooms(true);
    try {
      const params = new URLSearchParams({
        room_type: channelType,
        branch_id: String(branchId),
      });

      const res = await fetch(getApiUrl(`/api/v1/chat/rooms/?${params.toString()}`), {
        headers: {
          Accept: "application/json",
          "X-Branch-ID": String(branchId),
          ...(branchManager.token ? { Authorization: `Bearer ${branchManager.token}` } : {}),
        },
      });

      if (res.ok) {
        const data = await res.json();
        const list = Array.isArray(data) ? data : data.results || [];
        setRooms(list);
        if (list.length > 0) {
          setSelectedRoom(list[0]);
        } else {
          setSelectedRoom(null);
        }
      }
    } catch (err) {
      console.error("Branch chat rooms fetch error:", err);
    } finally {
      setLoadingRooms(false);
    }
  }, [branchId, branchManager.token, channelType, getApiUrl]);

  useEffect(() => {
    fetchRooms();
  }, [channelType, branchId, fetchRooms]);

  const handleCreateNewChannelRoom = async () => {
    try {
      const res = await fetch(getApiUrl("/api/v1/chat/rooms/create/"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-Branch-ID": String(branchId),
          ...(branchManager.token ? { Authorization: `Bearer ${branchManager.token}` } : {}),
        },
        body: JSON.stringify({
          room_type: channelType,
          branch_id: branchId,
        }),
      });

      if (res.ok) {
        const newRoom = await res.json();
        await fetchRooms();
        setSelectedRoom(newRoom);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const fetchMessages = useCallback(
    async (roomId: number) => {
      setLoadingMessages(true);
      try {
        const res = await fetch(getApiUrl(`/api/v1/chat/rooms/${roomId}/messages/`), {
          headers: {
            Accept: "application/json",
            "X-Branch-ID": String(branchId),
            ...(branchManager.token ? { Authorization: `Bearer ${branchManager.token}` } : {}),
          },
        });

        if (res.ok) {
          const data = await res.json();
          setMessages(Array.isArray(data) ? data : data.results || []);
          setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: "smooth" }), 100);
        }
      } catch (err) {
        console.error("Messages fetch error:", err);
      } finally {
        setLoadingMessages(false);
      }
    },
    [branchId, branchManager.token, getApiUrl]
  );

  useEffect(() => {
    if (!selectedRoom?.id) return;

    fetchMessages(selectedRoom.id);

    if (socketRef.current) socketRef.current.close();
    cleanupMedia();

    const ws = new WebSocket(getWsUrl(selectedRoom.id));
    socketRef.current = ws;

    ws.onmessage = async (event) => {
      const parsed = JSON.parse(event.data);
      const signalData = parsed.data?.data || parsed.data || {};

      if (parsed.type === "chat_message") {
        setMessages((prev) => {
          if (prev.some((m) => m.id === parsed.message.id)) return prev;
          return [...prev, parsed.message];
        });
      } else if (parsed.type === "call_offer") {
        if (String(signalData.sender_id) !== String(branchManager.id)) {
          setCallType(signalData.call_type || "audio");
          setCallState("ringing");
          (window as any).__pendingOffer = signalData.sdp;
        }
      } else if (parsed.type === "call_answer") {
        if (String(signalData.sender_id) !== String(branchManager.id)) {
          setCallState("active");
          if (peerConnectionRef.current && signalData.sdp) {
            await peerConnectionRef.current.setRemoteDescription(new RTCSessionDescription(signalData.sdp));
            while (pendingCandidatesRef.current.length > 0) {
              const cand = pendingCandidatesRef.current.shift();
              if (cand) await peerConnectionRef.current.addIceCandidate(new RTCIceCandidate(cand));
            }
          }
        }
      } else if (parsed.type === "ice_candidate") {
        if (String(signalData.sender_id) !== String(branchManager.id)) {
          const candidate = signalData.candidate;
          if (candidate) {
            if (peerConnectionRef.current && peerConnectionRef.current.remoteDescription) {
              await peerConnectionRef.current.addIceCandidate(new RTCIceCandidate(candidate));
            } else {
              pendingCandidatesRef.current.push(candidate);
            }
          }
        }
      } else if (parsed.type === "call_ended" || parsed.type === "call_rejected") {
        cleanupMedia();
        setCallState("idle");
      }
    };

    return () => {
      if (socketRef.current) socketRef.current.close();
      cleanupMedia();
    };
  }, [branchManager.id, cleanupMedia, fetchMessages, getWsUrl, selectedRoom?.id]);

  useEffect(() => {
    if (callState === "active") {
      if (localVideoRef.current && localStreamRef.current) {
        localVideoRef.current.srcObject = localStreamRef.current;
      }
      if (remoteVideoRef.current && remoteStreamRef.current) {
        remoteVideoRef.current.srcObject = remoteStreamRef.current;
      }
      if (remoteAudioRef.current && remoteStreamRef.current) {
        remoteAudioRef.current.srcObject = remoteStreamRef.current;
        remoteAudioRef.current.play().catch(() => {});
      }
    }
  }, [callState, callType]);

  const initiateCall = async (type: "audio" | "video") => {
    if (!socketRef.current || socketRef.current.readyState !== WebSocket.OPEN) return;
    setCallType(type);
    setCallState("calling");

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: true,
        video: type === "video",
      });
      localStreamRef.current = stream;

      const pc = createPeerConnection(socketRef.current, type);
      stream.getTracks().forEach((track) => pc.addTrack(track, stream));

      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);

      socketRef.current.send(
        JSON.stringify({
          type: "call_offer",
          data: { call_type: type, sdp: offer, sender_id: branchManager.id },
        })
      );
    } catch {
      alert("Microphone ba camera permission dewa hoyni.");
      cleanupMedia();
      setCallState("idle");
    }
  };

  const acceptCall = async () => {
    if (!socketRef.current || socketRef.current.readyState !== WebSocket.OPEN) return;
    setCallState("active");

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: true,
        video: callType === "video",
      });
      localStreamRef.current = stream;

      const pc = createPeerConnection(socketRef.current, callType);
      stream.getTracks().forEach((track) => pc.addTrack(track, stream));

      const pendingOffer = (window as any).__pendingOffer;
      if (pendingOffer) {
        await pc.setRemoteDescription(new RTCSessionDescription(pendingOffer));
        const answer = await pc.createAnswer();
        await pc.setLocalDescription(answer);

        socketRef.current.send(
          JSON.stringify({
            type: "call_answer",
            data: { sdp: answer, sender_id: branchManager.id },
          })
        );

        while (pendingCandidatesRef.current.length > 0) {
          const cand = pendingCandidatesRef.current.shift();
          if (cand) await pc.addIceCandidate(new RTCIceCandidate(cand));
        }
      }
    } catch {
      cleanupMedia();
      setCallState("idle");
    }
  };

  const endCall = () => {
    let finalStatus = "completed";
    if (callState === "calling") finalStatus = "cancelled";
    else if (callState === "ringing") finalStatus = "missed";

    socketRef.current?.send(
      JSON.stringify({
        type: "call_ended",
        data: {
          sender_id: branchManager.id,
          call_type: callType,
          duration: callDuration,
          status: finalStatus,
        },
      })
    );

    cleanupMedia();
    setCallState("idle");
  };

  const toggleMute = () => {
    if (localStreamRef.current) {
      const track = localStreamRef.current.getAudioTracks()[0];
      if (track) {
        track.enabled = !track.enabled;
        setIsMuted(!track.enabled);
      }
    }
  };

  const toggleVideoMute = () => {
    if (localStreamRef.current) {
      const track = localStreamRef.current.getVideoTracks()[0];
      if (track) {
        track.enabled = !track.enabled;
        setIsVideoMuted(!track.enabled);
      }
    }
  };

  const handleSendMessage = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!messageText.trim() || !selectedRoom) return;

    if (socketRef.current && socketRef.current.readyState === WebSocket.OPEN) {
      socketRef.current.send(
        JSON.stringify({
          type: "chat_message",
          text: messageText,
          sender_id: branchManager.id,
        })
      );
      setMessageText("");
    }
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !selectedRoom) return;

    const isImage = file.type.startsWith("image/");
    const isVideo = file.type.startsWith("video/");

    if (isImage && file.size > 30 * 1024 * 1024) {
      Swal.fire({
        icon: "error",
        title: "Size Limit Exceeded",
        text: "Image file size cannot exceed 30MB",
        background: isLight ? "#ffffff" : "#0f172a",
        color: isLight ? "#0f172a" : "#ffffff",
      });
      return;
    }

    if (isVideo && file.size > 100 * 1024 * 1024) {
      Swal.fire({
        icon: "error",
        title: "Size Limit Exceeded",
        text: "Video file size cannot exceed 100MB",
        background: isLight ? "#ffffff" : "#0f172a",
        color: isLight ? "#0f172a" : "#ffffff",
      });
      return;
    }

    const formData = new FormData();
    formData.append("file", file);
    formData.append("type", isImage ? "image" : isVideo ? "video" : "document");

    setSending(true);
    try {
      const res = await fetch(getApiUrl(`/api/v1/chat/rooms/${selectedRoom.id}/upload/`), {
        method: "POST",
        headers: {
          Accept: "application/json",
          "X-Branch-ID": String(branchId),
          ...(branchManager.token ? { Authorization: `Bearer ${branchManager.token}` } : {}),
        },
        body: formData,
      });

      if (res.ok) {
        const messageData = await res.json();
        socketRef.current?.send(
          JSON.stringify({
            type: "chat_file",
            message: messageData,
          })
        );
        setMessages((prev) => [...prev, messageData]);
      }
    } catch {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "File upload failed.",
        background: isLight ? "#ffffff" : "#0f172a",
        color: isLight ? "#0f172a" : "#ffffff",
      });
    } finally {
      setSending(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      let mimeType = "audio/webm";
      if (typeof MediaRecorder !== "undefined" && typeof MediaRecorder.isTypeSupported === "function") {
        if (MediaRecorder.isTypeSupported("audio/webm;codecs=opus")) {
          mimeType = "audio/webm;codecs=opus";
        } else if (MediaRecorder.isTypeSupported("audio/mp4")) {
          mimeType = "audio/mp4";
        }
      }

      const mediaRecorder = new MediaRecorder(stream, { mimeType });
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];
      isCancelledRef.current = false;

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) audioChunksRef.current.push(event.data);
      };

      mediaRecorder.onstop = async () => {
        stream.getTracks().forEach((track) => track.stop());
        if (isCancelledRef.current) return;

        const ext = mimeType.includes("mp4") ? "mp4" : "webm";
        const audioBlob = new Blob(audioChunksRef.current, { type: mimeType });
        if (audioBlob.size > 0 && selectedRoom?.id) {
          const audioFile = new File([audioBlob], `voice_${Date.now()}.${ext}`, { type: mimeType });
          const formData = new FormData();
          formData.append("file", audioFile);
          formData.append("type", "audio");

          const res = await fetch(getApiUrl(`/api/v1/chat/rooms/${selectedRoom.id}/upload/`), {
            method: "POST",
            headers: {
              "X-Branch-ID": String(branchId),
              ...(branchManager.token ? { Authorization: `Bearer ${branchManager.token}` } : {}),
            },
            body: formData,
          });

          if (res.ok) {
            const messageData = await res.json();
            socketRef.current?.send(
              JSON.stringify({
                type: "chat_file",
                message: messageData,
              })
            );
            setMessages((prev) => [...prev, messageData]);
          }
        }
      };

      mediaRecorder.start();
      setIsRecording(true);
      setRecordingDuration(0);
      recordingTimerRef.current = setInterval(() => setRecordingDuration((p) => p + 1), 1000);
    } catch {
      alert("Microphone permission allow kora lagbe.");
    }
  };

  const stopAndSendRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      clearInterval(recordingTimerRef.current);
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  const cancelRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      isCancelledRef.current = true;
      clearInterval(recordingTimerRef.current);
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      setRecordingDuration(0);
    }
  };

  // 🌟 Role Badges for Branch View
  const getBranchMessageMeta = useCallback(
    (msg: any) => {
      const sId = String(msg.sender_id || msg.sender || "");
      const isMe = sId === String(branchManager.id) && sId !== "";

      if (selectedRoom?.room_type === "branch_merchant") {
        if (isMe) {
          return {
            isMe: true,
            label: `🏪 You (${branchName || "Branch Outlet"})`,
            badgeColor: isLight
              ? "bg-emerald-100 text-emerald-900 border-emerald-300"
              : "bg-emerald-500/20 text-emerald-400 border-emerald-500/40",
            icon: Store,
          };
        } else {
          return {
            isMe: false,
            label: "🏢 Merchant HQ (Admin)",
            badgeColor: isLight
              ? "bg-amber-100 text-amber-900 border-amber-300"
              : "bg-amber-500/20 text-amber-400 border-amber-500/40",
            icon: Building2,
          };
        }
      } else {
        if (isMe) {
          return {
            isMe: true,
            label: `🏪 You (${branchName || "Outlet"} Support)`,
            badgeColor: isLight
              ? "bg-emerald-100 text-emerald-900 border-emerald-300"
              : "bg-emerald-500/20 text-emerald-400 border-emerald-500/40",
            icon: Store,
          };
        } else {
          return {
            isMe: false,
            label: `👤 Customer (${msg.sender_details?.name || "Buyer"})`,
            badgeColor: isLight
              ? "bg-blue-100 text-blue-900 border-blue-300"
              : "bg-blue-500/20 text-blue-400 border-blue-500/40",
            icon: Users,
          };
        }
      }
    },
    [branchManager.id, branchName, isLight, selectedRoom?.room_type]
  );

  const channelLabel = {
    branch_merchant: "HQ Merchant Chat (B2B)",
    branch_customer: "Branch to Customer Chat",
  }[channelType];

  return (
    <div
      style={{ ["--chat-primary" as any]: primaryColor }}
      className={`w-full h-[calc(100vh-140px)] min-h-[560px] flex flex-col font-sans rounded-3xl overflow-hidden border shadow-2xl relative transition-colors duration-200 ${
        isLight
          ? "bg-white border-slate-200 text-slate-800 shadow-slate-200/50"
          : "bg-[#080d17] border-slate-800 text-white shadow-2xl"
      }`}
    >
      <audio ref={remoteAudioRef} autoPlay playsInline className="hidden" />

      {/* WebRTC Calling Overlay */}
      {callState !== "idle" && (
        <div className="absolute inset-0 z-50 bg-slate-950/98 backdrop-blur-xl flex flex-col items-center justify-between py-10 px-6 text-white animate-in fade-in">
          <div className="text-center space-y-2">
            <span
              style={{
                color: primaryColor,
                backgroundColor: `${primaryColor}20`,
                borderColor: `${primaryColor}40`,
              }}
              className="text-[10px] uppercase font-black tracking-widest px-3 py-1 rounded-full border"
            >
              Realtime {callType.toUpperCase()} Call
            </span>
            <h2 className="text-2xl sm:text-3xl font-black mt-2">
              {selectedRoom?.room_type === "branch_merchant"
                ? "🏢 Merchant HQ (Admin)"
                : `👤 ${selectedRoom?.customer_details?.name || "Customer"}`}
            </h2>
            <p className="text-xs text-slate-400">
              {callState === "calling" && `Calling ${callType}...`}
              {callState === "ringing" && `Incoming ${callType} call...`}
              {callState === "active" && formatTimer(callDuration)}
            </p>
          </div>

          <div className="relative my-auto flex items-center justify-center">
            {callType === "video" && callState === "active" ? (
              <div className="relative w-72 sm:w-96 h-80 sm:h-[380px] bg-slate-900 rounded-3xl overflow-hidden border border-slate-800 shadow-2xl">
                <video ref={remoteVideoRef} autoPlay playsInline className="w-full h-full object-cover" />
                <video
                  ref={localVideoRef}
                  autoPlay
                  playsInline
                  muted
                  style={{ borderColor: primaryColor }}
                  className="w-24 h-32 object-cover absolute bottom-3 right-3 rounded-2xl border-2 bg-black"
                />
              </div>
            ) : (
              <div className="relative flex items-center justify-center">
                <div
                  style={{ backgroundColor: primaryColor }}
                  className="w-28 h-28 sm:w-32 sm:h-32 rounded-full flex items-center justify-center text-4xl font-black shadow-2xl border-4 border-slate-900 z-10 text-white"
                >
                  <Store className="w-12 h-12" />
                </div>
              </div>
            )}
          </div>

          <div className="flex items-center gap-5 sm:gap-6">
            {callState === "ringing" && (
              <>
                <button
                  onClick={endCall}
                  className="w-14 h-14 bg-rose-600 hover:bg-rose-500 rounded-full flex items-center justify-center shadow-lg cursor-pointer"
                  title="Decline"
                >
                  <PhoneOff className="w-6 h-6 text-white" />
                </button>
                <button
                  onClick={acceptCall}
                  style={{ backgroundColor: primaryColor }}
                  className="w-14 h-14 rounded-full flex items-center justify-center shadow-lg animate-bounce cursor-pointer text-white"
                  title="Answer"
                >
                  <PhoneCall className="w-6 h-6" />
                </button>
              </>
            )}

            {callState === "calling" && (
              <button
                onClick={endCall}
                className="w-14 h-14 bg-rose-600 hover:bg-rose-500 rounded-full flex items-center justify-center shadow-xl cursor-pointer"
                title="Cancel Call"
              >
                <PhoneOff className="w-6 h-6 text-white" />
              </button>
            )}

            {callState === "active" && (
              <>
                <button
                  onClick={toggleMute}
                  className={`w-12 h-12 rounded-full flex items-center justify-center cursor-pointer ${
                    isMuted ? "bg-rose-600 text-white" : "bg-slate-800 text-slate-300"
                  }`}
                >
                  <MicOff className="w-5 h-5" />
                </button>
                {callType === "video" && (
                  <button
                    onClick={toggleVideoMute}
                    className={`w-12 h-12 rounded-full flex items-center justify-center cursor-pointer ${
                      isVideoMuted ? "bg-rose-600 text-white" : "bg-slate-800 text-slate-300"
                    }`}
                  >
                    <VideoOff className="w-5 h-5" />
                  </button>
                )}
                <button
                  onClick={endCall}
                  className="w-14 h-14 bg-rose-600 hover:bg-rose-500 rounded-full flex items-center justify-center shadow-xl cursor-pointer"
                >
                  <PhoneOff className="w-6 h-6 text-white" />
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {/* Top Header */}
      <div
        className={`p-4 border-b flex flex-wrap items-center justify-between gap-3 shrink-0 transition-colors ${
          isLight
            ? "bg-slate-50 border-slate-200 text-slate-800"
            : "bg-[#0c1322] border-slate-800 text-white"
        }`}
      >
        <div className="flex items-center gap-3">
          <div
            style={{
              backgroundColor: `${primaryColor}18`,
              color: primaryColor,
              borderColor: `${primaryColor}35`,
            }}
            className="w-10 h-10 rounded-2xl flex items-center justify-center border"
          >
            {channelType === "branch_merchant" ? (
              <Building2 className="w-5 h-5" />
            ) : (
              <Users className="w-5 h-5" />
            )}
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h2 className={`text-sm font-black tracking-wide ${isLight ? "text-slate-900" : "text-white"}`}>
                {channelLabel}
              </h2>
              <span className="text-[9px] font-black uppercase px-2 py-0.5 rounded-md border bg-emerald-500/10 text-emerald-500 border-emerald-500/30">
                Outlet Portal Mode
              </span>
            </div>
            <p className={`text-[11px] ${isLight ? "text-slate-500" : "text-slate-400"}`}>
              Branch ID: #{branchId} • {branchName || "Branch Store"}
            </p>
          </div>
        </div>

        {/* Dropdown */}
        <div className="relative">
          <button
            type="button"
            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
            className={`px-4 py-2 rounded-2xl border text-xs font-bold flex items-center gap-2 cursor-pointer shadow-xs transition-all ${
              isLight
                ? "bg-white border-slate-300 text-slate-800 hover:border-slate-400"
                : "bg-[#11192b] border-slate-700 text-slate-200 hover:border-slate-500"
            }`}
          >
            <span>{channelLabel}</span>
            <ChevronDown className={`w-3.5 h-3.5 transition-transform ${isDropdownOpen ? "rotate-180" : ""}`} />
          </button>

          {isDropdownOpen && (
            <div
              className={`absolute right-0 mt-2 w-64 border rounded-2xl shadow-2xl p-2 z-50 space-y-1 animate-fadeIn ${
                isLight ? "bg-white border-slate-200 text-slate-800" : "bg-[#0f172a] border-slate-700 text-white"
              }`}
            >
              <button
                type="button"
                onClick={() => {
                  setChannelType("branch_merchant");
                  setSelectedRoom(null);
                  setIsDropdownOpen(false);
                }}
                style={channelType === "branch_merchant" ? { backgroundColor: primaryColor, color: "#fff" } : {}}
                className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-xl text-xs font-bold text-left cursor-pointer transition-colors ${
                  channelType === "branch_merchant"
                    ? ""
                    : isLight
                    ? "text-slate-700 hover:bg-slate-100"
                    : "text-slate-300 hover:bg-slate-800"
                }`}
              >
                <Building2 className="w-4 h-4" />
                <span>HQ Merchant Chat (B2B)</span>
              </button>

              <button
                type="button"
                onClick={() => {
                  setChannelType("branch_customer");
                  setSelectedRoom(null);
                  setIsDropdownOpen(false);
                }}
                style={channelType === "branch_customer" ? { backgroundColor: primaryColor, color: "#fff" } : {}}
                className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-xl text-xs font-bold text-left cursor-pointer transition-colors ${
                  channelType === "branch_customer"
                    ? ""
                    : isLight
                    ? "text-slate-700 hover:bg-slate-100"
                    : "text-slate-300 hover:bg-slate-800"
                }`}
              >
                <Store className="w-4 h-4" />
                <span>Branch to Customer Chat</span>
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Main Split */}
      <div className="flex-1 flex overflow-hidden">
        {/* Rooms Sidebar */}
        <div
          className={`w-80 border-r flex flex-col shrink-0 transition-colors ${
            isLight ? "bg-slate-50/80 border-slate-200" : "bg-[#090e1a] border-slate-800"
          }`}
        >
          <div className={`p-3 border-b ${isLight ? "border-slate-200" : "border-slate-800"}`}>
            <div className="relative">
              <Search className={`w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 ${isLight ? "text-slate-400" : "text-slate-500"}`} />
              <input
                type="text"
                placeholder="Search rooms..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className={`w-full pl-9 pr-3 py-2 rounded-xl text-xs outline-none transition-all ${
                  isLight
                    ? "bg-white border border-slate-200 text-slate-800 placeholder:text-slate-400 focus:border-slate-400"
                    : "bg-[#11192b] border border-slate-800 text-white placeholder:text-slate-500 focus:border-slate-500"
                }`}
              />
            </div>
          </div>

          <div className={`flex-1 overflow-y-auto divide-y ${isLight ? "divide-slate-200/60" : "divide-slate-800/60"}`}>
            {loadingRooms ? (
              <div className="p-8 text-center text-slate-400 text-xs">
                <Loader2 style={{ color: primaryColor }} className="w-5 h-5 animate-spin mx-auto mb-2" />
                <span>Loading active channels...</span>
              </div>
            ) : rooms.length === 0 ? (
              <div className="p-8 text-center text-slate-400 text-xs space-y-3">
                <p>No active sessions found.</p>
                <button
                  type="button"
                  onClick={handleCreateNewChannelRoom}
                  style={{ backgroundColor: primaryColor }}
                  className="px-3.5 py-2 rounded-xl text-white text-xs font-bold inline-flex items-center gap-1.5 shadow-md cursor-pointer hover:opacity-90 active:scale-95 transition-all"
                >
                  <PlusCircle className="w-3.5 h-3.5" />
                  <span>Start Channel</span>
                </button>
              </div>
            ) : (
              rooms.map((room) => {
                const isSelected = selectedRoom?.id === room.id;
                const isB2B = room.room_type === "branch_merchant";
                const displayTitle = isB2B ? "🏢 Merchant HQ (Admin)" : (room.customer_details?.name || "Customer");

                return (
                  <div
                    key={room.id}
                    onClick={() => setSelectedRoom(room)}
                    style={
                      isSelected
                        ? {
                            borderLeftColor: primaryColor,
                            backgroundColor: `${primaryColor}14`,
                          }
                        : {}
                    }
                    className={`p-3.5 flex items-center justify-between cursor-pointer transition-colors ${
                      isSelected
                        ? "border-l-4 font-bold"
                        : isLight
                        ? "hover:bg-slate-100/70"
                        : "hover:bg-slate-900/60"
                    }`}
                  >
                    <div className="min-w-0 pr-2">
                      <div className="flex items-center gap-1.5 mb-0.5">
                        <span
                          className={`px-1.5 py-0.2 rounded text-[8px] font-black uppercase border ${
                            isB2B
                              ? "bg-amber-500/15 text-amber-500 border-amber-500/30"
                              : "bg-blue-500/15 text-blue-500 border-blue-500/30"
                          }`}
                        >
                          {isB2B ? "🏢 HQ Merchant" : "👤 Customer"}
                        </span>
                      </div>
                      <h4 className={`text-xs font-bold truncate ${isLight ? "text-slate-900" : "text-white"}`}>
                        {displayTitle}
                      </h4>
                      <p className={`text-[11px] truncate mt-0.5 ${isLight ? "text-slate-500" : "text-slate-400"}`}>
                        {room.last_message?.text || "Click to open conversation"}
                      </p>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* Conversation Box */}
        <div
          className={`flex-1 flex flex-col min-w-0 transition-colors ${
            isLight ? "bg-[#f8fafc]" : "bg-[#070b13]"
          }`}
        >
          {selectedRoom ? (
            <>
              {/* Header */}
              <div
                className={`p-3.5 px-5 border-b flex items-center justify-between shrink-0 transition-colors ${
                  isLight ? "bg-white border-slate-200" : "bg-[#0a101d] border-slate-800"
                }`}
              >
                <div>
                  <h3 className={`text-xs font-black ${isLight ? "text-slate-900" : "text-white"}`}>
                    {selectedRoom.room_type === "branch_merchant"
                      ? "🏢 Merchant HQ (Admin Office)"
                      : `👤 ${selectedRoom.customer_details?.name || "Customer"}`}
                  </h3>
                  <span style={{ color: primaryColor }} className="text-[10px] font-mono font-bold">
                    Channel #{selectedRoom.id} • {selectedRoom.room_type.toUpperCase()}
                  </span>
                </div>

                <div className="flex items-center gap-2">
                  <button
                    onClick={() => initiateCall("audio")}
                    style={{ color: primaryColor }}
                    className={`p-2 border rounded-xl transition-all cursor-pointer ${
                      isLight
                        ? "bg-slate-100 border-slate-200 hover:bg-slate-200"
                        : "bg-slate-900 border-slate-800 hover:bg-slate-800"
                    }`}
                    title="Voice Call"
                  >
                    <Phone className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => initiateCall("video")}
                    style={{ color: primaryColor }}
                    className={`p-2 border rounded-xl transition-all cursor-pointer ${
                      isLight
                        ? "bg-slate-100 border-slate-200 hover:bg-slate-200"
                        : "bg-slate-900 border-slate-800 hover:bg-slate-800"
                    }`}
                    title="Video Call"
                  >
                    <Video className="w-4 h-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => fetchMessages(selectedRoom.id)}
                    className={`p-2 rounded-xl transition-colors cursor-pointer ${
                      isLight ? "text-slate-500 hover:bg-slate-100" : "text-slate-400 hover:text-white hover:bg-slate-800"
                    }`}
                    title="Refresh Messages"
                  >
                    <RefreshCw className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>

              {/* Messages History */}
              <div className="flex-1 overflow-y-auto p-4 space-y-3.5">
                {loadingMessages ? (
                  <div className="py-20 text-center text-slate-400 text-xs">
                    <Loader2 style={{ color: primaryColor }} className="w-6 h-6 animate-spin mx-auto mb-2" />
                    <span>Loading conversation history...</span>
                  </div>
                ) : messages.length === 0 ? (
                  <div className="py-20 text-center text-slate-400 text-xs">
                    No messages yet. Send a message to start!
                  </div>
                ) : (
                  messages.map((msg, idx) => {
                    const meta = getBranchMessageMeta(msg);
                    const MetaIcon = meta.icon;

                    return (
                      <div key={idx} className={`flex flex-col ${meta.isMe ? "items-end" : "items-start"}`}>
                        <div className={`flex items-center gap-1.5 mb-1 ${meta.isMe ? "flex-row-reverse" : "flex-row"}`}>
                          <span
                            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[9px] font-black uppercase tracking-wider border ${meta.badgeColor}`}
                          >
                            <MetaIcon className="w-2.5 h-2.5" />
                            <span>{meta.label}</span>
                          </span>
                        </div>

                        <div
                          style={meta.isMe ? { backgroundColor: primaryColor, color: "#ffffff" } : {}}
                          className={`max-w-[75%] sm:max-w-[70%] p-3 rounded-2xl text-xs space-y-1.5 leading-relaxed shadow-sm ${
                            meta.isMe
                              ? "rounded-tr-none text-white shadow-md"
                              : isLight
                              ? "bg-white text-slate-800 border border-slate-200/90 rounded-tl-none"
                              : "bg-[#111a2d] text-slate-200 border border-slate-800 rounded-tl-none"
                          }`}
                        >
                          {msg.text && <FormattedChatText content={msg.text} isLight={isLight && !meta.isMe} />}

                          {msg.message_type === "image" && msg.file_url && (
                            <img src={msg.file_url} alt="img" className="rounded-xl max-h-60 w-auto object-cover" />
                          )}
                          {msg.message_type === "video" && msg.file_url && (
                            <video controls src={msg.file_url} className="max-h-60 w-full rounded-xl" />
                          )}
                          {msg.message_type === "audio" && msg.file_url && (
                            <audio src={msg.file_url} controls preload="auto" className="h-8 max-w-[170px]" />
                          )}

                          <span className="text-[9px] opacity-75 font-mono block text-right">
                            {new Date(msg.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                          </span>
                        </div>
                      </div>
                    );
                  })
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* Input Bar */}
              <div
                className={`p-3 border-t flex-shrink-0 transition-colors ${
                  isLight ? "bg-white border-slate-200" : "bg-[#0a101d] border-slate-800"
                }`}
              >
                {isRecording ? (
                  <div
                    className={`flex items-center justify-between border rounded-full px-4 py-2 animate-in fade-in ${
                      isLight ? "bg-rose-50 border-rose-200" : "bg-slate-900 border-rose-500/40"
                    }`}
                  >
                    <span className={`text-xs font-bold ${isLight ? "text-rose-700" : "text-white"}`}>
                      Recording: {formatTimer(recordingDuration)}
                    </span>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={cancelRecording}
                        className={`p-1.5 rounded-full transition-colors cursor-pointer ${
                          isLight ? "text-rose-600 hover:bg-rose-100" : "text-rose-400 hover:bg-rose-500/10"
                        }`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                      <button
                        type="button"
                        onClick={stopAndSendRecording}
                        style={{ backgroundColor: primaryColor }}
                        className="p-2 text-white rounded-full shadow-md cursor-pointer hover:opacity-90"
                      >
                        <Send className="w-3.5 h-3.5 ml-0.5" />
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <input type="file" ref={fileInputRef} onChange={handleFileUpload} className="hidden" />

                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      className={`p-2.5 rounded-xl transition-colors cursor-pointer ${
                        isLight
                          ? "text-slate-400 hover:bg-slate-100 hover:text-slate-700"
                          : "text-slate-400 hover:text-white hover:bg-slate-800"
                      }`}
                      title="Attach File"
                    >
                      <Paperclip className="w-4 h-4" />
                    </button>

                    <form onSubmit={handleSendMessage} className="flex-1 flex items-center gap-2">
                      <input
                        type="text"
                        value={messageText}
                        onChange={(e) => setMessageText(e.target.value)}
                        placeholder="Type message as Branch Manager..."
                        className={`flex-1 px-4 py-2.5 rounded-2xl border text-xs outline-none transition-all ${
                          isLight
                            ? "bg-slate-50 border-slate-200 text-slate-800 placeholder:text-slate-400 focus:bg-white focus:border-slate-300"
                            : "bg-[#11192b] border-slate-800 text-white placeholder:text-slate-500 focus:border-slate-500"
                        }`}
                      />

                      {messageText.trim() ? (
                        <button
                          type="submit"
                          style={{ backgroundColor: primaryColor }}
                          className="p-2.5 rounded-2xl text-white transition-all cursor-pointer shadow-md hover:opacity-90"
                        >
                          <Send className="w-4 h-4" />
                        </button>
                      ) : (
                        <button
                          type="button"
                          onClick={startRecording}
                          style={{ backgroundColor: `${primaryColor}18`, color: primaryColor }}
                          className="p-2.5 rounded-2xl border border-transparent hover:opacity-90 transition-all cursor-pointer"
                        >
                          <Mic className="w-4 h-4" />
                        </button>
                      )}
                    </form>
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className="flex-1 flex flex-col items-center justify-center p-8 text-center text-slate-400 text-xs space-y-3">
              <MessageSquare className="w-10 h-10 mb-1 opacity-30" />
              <p>No active conversation. Tap below to connect with Merchant HQ.</p>
              <button
                type="button"
                onClick={handleCreateNewChannelRoom}
                style={{ backgroundColor: primaryColor }}
                className="px-4 py-2 rounded-xl text-white font-bold inline-flex items-center gap-1.5 shadow-md cursor-pointer hover:opacity-90 active:scale-95 transition-all"
              >
                <PlusCircle className="w-4 h-4" />
                <span>Connect With HQ</span>
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default BranchChatPanel;